import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { ArrowBack, Edit, Delete, Bookmark, Share, Flag, MoreVert } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import CreatePostModal from '../components/community/CreatePostModal';
import EditCommunityModal from '../components/communityDetail/EditCommunityModal';
import { communityApi } from '../services/communityApi';
import { postApi } from '../services/postApi';

// Import components
import CommunityHeader from '../components/communityDetail/CommunityHeader';
import CommunityTabs from '../components/communityDetail/CommunityTabs';
import PostsTab from '../components/communityDetail/PostsTab';
import AboutTab from '../components/communityDetail/AboutTab';
import MembersTab from '../components/communityDetail/MembersTab';
import RulesTab from '../components/communityDetail/RulesTab';

// Import interfaces
import { Community, Post } from '../types/community';

// NEW: Import utility function
import { getUserCommunityRole } from '../utils/communityUtils';
import { CommunityDetailSkeleton } from '../components/skeletons';

interface CommunityMember {
  id: string;
  user: {
    id: string;
    username: string;
    profilePicture?: string;
    createdAt: string;
  };
  role: 'owner' | 'moderator' | 'member';
  joinedAt: string;
  notificationsEnabled: boolean;
}

const CommunityDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const videoRefs = useRef<{[key: string]: HTMLVideoElement | null}>({});

  const [community, setCommunity] = useState<Community | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [sortBy, setSortBy] = useState('new');
  const [postSortBy, setPostSortBy] = useState<'desc' | 'asc'>('desc');
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false);
  const [editCommunityModalOpen, setEditCommunityModalOpen] = useState(false); 
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [postMenuAnchor, setPostMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [videoMuted, setVideoMuted] = useState<{[key: string]: boolean}>({});
  
  // NEW: User role state
  const [userRoleData, setUserRoleData] = useState<{
    isOwner: boolean;
    isMember: boolean;
    role: string | null;
    canEdit: boolean;
    communityId: string;
    communityName: string;
  } | null>(null);

  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)';
  
  const gradientButtonStyle = {
    background: isDark 
      ? `linear-gradient(135deg, ${goldColor} 0%, ${alpha(goldColor, 0.8)} 100%)`
      : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '12px',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: `0 4px 20px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.3)}`,
      background: isDark 
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, ${alpha(goldColor, 0.7)} 100%)`
        : 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)',
      transform: 'translateY(-1px)'
    }
  };

  const fetchCommunity = async () => {
  try {
    setLoading(true);
    const response = await communityApi.getCommunity(slug || '');
    
    if (response.success) {
      // Set community data first
      setCommunity(response.community);
      
      // ALWAYS fetch user role data for logged-in users
      if (user) {
        try {
          const roleResponse = await communityApi.checkUserRole(response.community.id);
          setUserRoleData(roleResponse.data);
          
          // OVERRIDE community data with accurate role data
          setCommunity(prev => {
            if (!prev) return null;
            return {
              ...prev,
              isMember: roleResponse.data.isMember,
              userRole: roleResponse.data.role,
              canEdit: roleResponse.data.canEdit
            };
          });
        } catch (roleError) {
          console.error('Failed to fetch user role:', roleError);
          // Keep community data as-is if role check fails
        }
      }
      
      fetchPosts(response.community.id);
      fetchMembers(response.community.id);
    } else {
      toast.error(response.error || 'Community not found');
      navigate('/community');
    }
  } catch (error) {
    console.error('Failed to fetch community:', error);
    toast.error('Failed to load community');
    navigate('/community');
  } finally {
    setLoading(false);
  }
};

  const fetchPosts = async (communityId: string) => {
    try {
      setPostsLoading(true);
      const response = await communityApi.getCommunityPosts(communityId, {
        sortBy: sortBy === 'new' ? 'new' : sortBy === 'top' ? 'top' : 'hot',
        page: 1,
        limit: 20
      });
      
      if (response.success) {
        setPosts(response.posts || []);
      } else {
        toast.error(response.error || 'Failed to fetch posts');
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load posts');
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const fetchMembers = async (communityId: string) => {
    try {
      setMembersLoading(true);
      const response = await communityApi.getCommunityMembers(communityId, {
        page: 1,
        limit: 50
      });
      
      if (response.success) {
        setMembers(response.members || []);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const fetchUserRole = async (communityId: string) => {
    if (!user) return;
    
    try {
      const response = await communityApi.checkUserRole(communityId);
      setUserRoleData(response.data);
    } catch (error) {
      console.error('Failed to fetch user role:', error);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchCommunity();
    }
  }, [slug]);

  useEffect(() => {
    if (community) {
      fetchPosts(community.id);
    }
  }, [sortBy, community]);

  // NEW: Handle successful community edit
  const handleCommunityEdited = (updatedCommunity: Community) => {
    setCommunity(updatedCommunity);
    toast.success('Community updated successfully!');
    setEditCommunityModalOpen(false);
  };

  // --- COMPLETE JOIN / LEAVE LOGIC ---
const handleJoinLeave = async () => {
  if (!community || !user) {
    toast.error("Please login to join this community");
    return;
  }

  console.log('🔍 State Check:', {
    communityIsMember: community.isMember,
    userRoleDataIsMember: userRoleData?.isMember,
    calculatedIsMember: isMember,
    calculatedIsOwner: isOwner,
    userRole: community.userRole
  });

  // Use the most accurate isMember value
  const actualIsMember = userRoleData?.isMember !== undefined 
    ? userRoleData.isMember 
    : community.isMember;

  const actualIsOwner = userRoleData?.isOwner !== undefined
    ? userRoleData.isOwner
    : (community.userRole === 'owner');

  console.log('🎯 Decision:', {
    actualIsMember,
    actualIsOwner,
    shouldJoin: !actualIsMember && !actualIsOwner,
    shouldLeave: actualIsMember && !actualIsOwner
  });

  try {
    if (actualIsMember && !actualIsOwner) {
      // LEAVE - User is a member and not an owner
      console.log('🚪 Calling LEAVE endpoint...');
      const response = await communityApi.leaveCommunity(community.id);
      
      if (response.success) {
        toast.success('Successfully left community');
        
        // Update community state
        setCommunity(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isMember: false,
            userRole: null,
            membersCount: Math.max(0, prev.membersCount - 1)
          };
        });
        
        // Update user role data
        setUserRoleData(prev => prev ? {
          ...prev,
          isMember: false,
          isOwner: false,
          role: null,
          canEdit: false
        } : null);
        
        // Remove user from members list
        setMembers(prev => prev.filter(m => m.user.id !== user._id));
      } else {
        toast.error(response.error || 'Failed to leave community');
      }
      
    } else if (!actualIsMember && !actualIsOwner) {
      // JOIN - User is not a member and not an owner
      console.log('🤝 Calling JOIN endpoint...');
      const response = await communityApi.joinCommunity(community.id);
      
      if (response.success) {
        toast.success('Successfully joined community');
        
        // Update community state
        setCommunity(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isMember: true,
            userRole: 'member',
            membersCount: prev.membersCount + 1
          };
        });
        
        // Update user role data
        setUserRoleData(prev => prev ? {
          ...prev,
          isMember: true,
          role: 'member',
          canEdit: false
        } : {
          isOwner: false,
          isMember: true,
          role: 'member',
          canEdit: false,
          communityId: community.id,
          communityName: community.name
        });
        
        // Refresh members list
        fetchMembers(community.id);
      } else {
        toast.error(response.error || 'Failed to join community');
      }
      
    } else if (actualIsOwner) {
      // Owner - show appropriate message
      console.log('👑 Owner detected - cannot join/leave');
      toast.info('You are the owner of this community.');
      return;
    }
  } catch (error: any) {
    console.error('❌ Join/Leave error:', error);
    
    const errorMessage = error.response?.data?.error || error.message || 'Operation failed';
    
    // Handle specific error cases
    if (error.response?.status === 400) {
      if (errorMessage.includes('Already a member') || errorMessage.includes('already a member')) {
        // Update state to reflect actual membership
        setCommunity(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isMember: true,
            userRole: 'member'
          };
        });
        
        setUserRoleData(prev => prev ? {
          ...prev,
          isMember: true,
          role: 'member'
        } : null);
        
        toast.success('You are already a member of this community');
      } else if (errorMessage.includes('owner') || errorMessage.includes('Owner')) {
        // Update state to reflect ownership
        setCommunity(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isMember: true,
            userRole: 'owner'
          };
        });
        
        setUserRoleData(prev => prev ? {
          ...prev,
          isMember: true,
          isOwner: true,
          role: 'owner',
          canEdit: true
        } : null);
        
        toast.info('You are the owner of this community');
      } else {
        toast.error(errorMessage);
      }
    } else if (error.response?.status === 404) {
      toast.error('Community not found');
      navigate('/community');
    } else if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action');
    } else {
      toast.error('Failed to process your request. Please try again.');
    }
  }
};

  const handleVote = async (postId: string, vote: 'up' | 'down' | 'remove') => {
    if (!user) {
      toast.error('Please login to vote');
      return;
    }

    try {
      await postApi.votePost(postId, vote);
      
      setPosts(posts.map(post => {
        if (post.id === postId) {
          let newUpvotes = post.upvotes;
          let newDownvotes = post.downvotes;
          
          if (post.userVote === 'up') newUpvotes -= 1;
          if (post.userVote === 'down') newDownvotes -= 1;
          
          if (vote === 'up') newUpvotes += 1;
          if (vote === 'down') newDownvotes += 1;
          
          return {
            ...post,
            upvotes: newUpvotes,
            downvotes: newDownvotes,
            score: newUpvotes - newDownvotes,
            userVote: vote === 'remove' ? null : vote
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Failed to vote on post');
    }
  };

  const handleCreatePost = () => {
    if (!user) {
      toast.error('Please login to create posts');
      return;
    }
    setCreatePostModalOpen(true);
  };

  const handlePostCreated = (newPost: any) => {
    const formattedPost: Post = {
      id: newPost.id,
      title: newPost.title,
      content: newPost.content,
      type: newPost.type,
      mediaUrl: newPost.mediaUrl,
      mediaType: newPost.mediaType,
      images: [],
      author: {
        id: user?._id || '',
        username: user?.username || 'You',
        profilePicture: user?.profilePicture
      },
      community: {
        id: community?.id || '',
        name: community?.name || '',
        slug: community?.slug || '',
        profilePicture: community?.profilePicture
      },
      tags: newPost.tags || [],
      upvotes: 0,
      downvotes: 0,
      score: 0,
      commentsCount: 0,
      isPublic: true,
      createdAt: new Date().toISOString(),
      userVote: null,
      viewCount: 0
    };
    
    setPosts([formattedPost, ...posts]);
    setCreatePostModalOpen(false);
    toast.success('Post created successfully!');
    
    if (community) {
      setCommunity({
        ...community,
        postsCount: community.postsCount + 1
      });
    }
  };

  // NEW: Handle edit community
  const handleEditCommunity = () => {
    if (!userRoleData?.canEdit && !(community?.canEdit || community?.userRole === 'owner')) {
      toast.error('Only community owner can edit community');
      return;
    }
    setEditCommunityModalOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePostMenuOpen = (event: React.MouseEvent<HTMLElement>, postId: string) => {
    setPostMenuAnchor(event.currentTarget);
    setSelectedPostId(postId);
  };

  const handlePostMenuClose = () => {
    setPostMenuAnchor(null);
    setSelectedPostId(null);
  };

  const toggleVideoMute = (postId: string) => {
    const video = videoRefs.current[postId];
    if (video) {
      video.muted = !video.muted;
      setVideoMuted(prev => ({
        ...prev,
        [postId]: video.muted
      }));
    }
  };

  const handleVideoRef = (postId: string, el: HTMLVideoElement | null) => {
    videoRefs.current[postId] = el;
    if (el) {
      el.loop = true;
      el.autoplay = true;
      el.playsInline = true;
      el.muted = videoMuted[postId] || true;
    }
  };

  // --- BUTTON VISIBILITY LOGIC ---
// Use userRoleData as primary source, fallback to community data
const isOwner = userRoleData?.isOwner !== undefined 
  ? userRoleData.isOwner 
  : (community?.userRole === 'owner');

const isMember = userRoleData?.isMember !== undefined
  ? userRoleData.isMember
  : (community?.isMember || false);

const canEdit = userRoleData?.canEdit !== undefined
  ? userRoleData.canEdit
  : (community?.canEdit || false);

// Show Join Button: User exists AND community loaded AND is NOT a member AND not owner
const showJoinButton = user && community && !isMember && !isOwner;

// Show Leave Button: User exists AND community loaded AND is a member AND not owner
const showLeaveButton = user && community && isMember && !isOwner;

// Show Edit Button: User is owner OR has edit permission
const showEditButton = user && community && (isOwner || canEdit);

  if (loading) {
    return <CommunityDetailSkeleton />;
  }

  if (!community) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: isDark ? '#000000' : '#FAFAFA',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Box sx={{ textAlign: 'center', ...glassEffect, p: 6, borderRadius: '20px' }}>
          <Typography variant="h5" sx={{ mb: 2, color: isDark ? '#fff' : deepGreen }}>
            Community not found
          </Typography>
          <Button 
            onClick={() => navigate('/community')} 
            variant="contained"
            sx={{
              ...gradientButtonStyle,
              px: 4,
              py: 1.5
            }}
          >
            Browse Communities
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      '& *::-webkit-scrollbar': { display: 'none' },
      '& *': { msOverflowStyle: 'none', scrollbarWidth: 'none' }
    }}>
      {/* Community Banner - Full Width */}
      <Box sx={{ position: 'relative', mb: { xs: 2, md: 4 }, width: '100%' }}>
        {community.bannerImage ? (
          <Box
            component="img"
            src={community.bannerImage}
            alt={community.name}
            sx={{
              width: '100%',
              height: { xs: 150, sm: 200, md: 300 },
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: { xs: 150, sm: 200, md: 300 },
              bgcolor: isDark 
                ? `linear-gradient(135deg, ${alpha(goldColor, 0.2)} 0%, ${alpha(deepGreen, 0.5)} 100%)`
                : `linear-gradient(135deg, ${alpha(goldColor, 0.3)} 0%, ${alpha('#1D5C4A', 0.3)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '4rem' },
                color: isDark ? alpha('#fff', 0.8) : alpha(deepGreen, 0.8),
                textShadow: isDark 
                  ? `0 4px 8px ${alpha('#000', 0.3)}`
                  : `0 4px 8px ${alpha('#fff', 0.3)}`,
                textAlign: 'center',
                px: 2
              }}
            >
              {community.name}
            </Typography>
          </Box>
        )}
        
        {/* Back Button */}
        <IconButton
          onClick={() => navigate('/community')}
          sx={{
            position: 'absolute',
            top: { xs: 8, md: 20 },
            left: { xs: 8, md: 20 },
            bgcolor: alpha('#000', 0.5),
            color: '#fff',
            '&:hover': {
              bgcolor: alpha('#000', 0.7)
            },
            width: { xs: 36, md: 48 },
            height: { xs: 36, md: 48 }
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
        </IconButton>
      </Box>

      {/* Main Content Area - CENTERED */}
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 4 }, mt: { xs: -15, sm: -18, md: -20 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
          <Grid size={{xs : 12, md:10, lg:8}} >
            
            {/* Community Header Card */}
            <CommunityHeader
              community={community}
              isDark={isDark}
              goldColor={goldColor}
              deepGreen={deepGreen}
              glassEffect={glassEffect}
              navigate={navigate}
              handleJoinLeave={handleJoinLeave}
              handleMenuOpen={handleMenuOpen}
              handleMenuClose={handleMenuClose}
              anchorEl={anchorEl}
              isOwner={isOwner}
              showJoinButton={showJoinButton}
              showLeaveButton={showLeaveButton}
              showEditButton={showEditButton}
              handleEditCommunity={handleEditCommunity}
              isMobile={isMobile}
            />

            {/* Tabs Section */}
            <CommunityTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isDark={isDark}
              goldColor={goldColor}
              deepGreen={deepGreen}
              glassEffect={glassEffect}
              lokiGradient={lokiGradient}
              isMobile={isMobile}
            />

            {/* Posts Section */}
            {activeTab === 0 && (
              <PostsTab
                posts={posts}
                postsLoading={postsLoading}
                community={community}
                sortBy={sortBy}
                setSortBy={setSortBy}
                postSortBy={postSortBy}
                setPostSortBy={setPostSortBy}
                isDark={isDark}
                goldColor={goldColor}
                deepGreen={deepGreen}
                glassEffect={glassEffect}
                gradientButtonStyle={gradientButtonStyle}
                handleCreatePost={handleCreatePost}
                handleVote={handleVote}
                handlePostMenuOpen={handlePostMenuOpen}
                navigate={navigate}
                toggleVideoMute={toggleVideoMute}
                handleVideoRef={handleVideoRef}
                videoMuted={videoMuted}
                isMobile={isMobile}
                isMember={isMember}
              />
            )}

            {/* About Tab */}
            {activeTab === 1 && community && (
              <AboutTab
                community={community}
                isDark={isDark}
                goldColor={goldColor}
                deepGreen={deepGreen}
                glassEffect={glassEffect}
              />
            )}

            {/* Members Tab */}
            {activeTab === 2 && (
              <MembersTab
                members={members}
                membersLoading={membersLoading}
                isDark={isDark}
                goldColor={goldColor}
                deepGreen={deepGreen}
                glassEffect={glassEffect}
              />
            )}

            {/* Rules Tab */}
            {activeTab === 3 && (
              <RulesTab
                community={{...community, rules: community.rules || []}}
                user={user}
                isDark={isDark}
                goldColor={goldColor}
                deepGreen={deepGreen}
                glassEffect={glassEffect}
              />
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Post Menu */}
      <Menu
        anchorEl={postMenuAnchor}
        open={Boolean(postMenuAnchor)}
        onClose={handlePostMenuClose}
        PaperProps={{
          sx: {
            ...glassEffect,
            borderRadius: '12px',
            mt: 1
          }
        }}
      >
        {selectedPostId && posts.find(p => p.id === selectedPostId)?.author.id === user?._id && (
          <>
            <MenuItem onClick={handlePostMenuClose}>
              <ListItemIcon>
                <Edit fontSize="small" sx={{ color: goldColor }} />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Edit Post</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePostMenuClose} sx={{ color: '#ff4444' }}>
              <ListItemIcon>
                <Delete fontSize="small" sx={{ color: '#ff4444' }} />
              </ListItemIcon>
              <ListItemText>Delete Post</ListItemText>
            </MenuItem>
            <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
          </>
        )}
        <MenuItem onClick={handlePostMenuClose}>
          <ListItemIcon>
            <Bookmark fontSize="small" sx={{ color: goldColor }} />
          </ListItemIcon>
          <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Save Post</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePostMenuClose}>
          <ListItemIcon>
            <Share fontSize="small" sx={{ color: goldColor }} />
          </ListItemIcon>
          <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Share Post</ListItemText>
        </MenuItem>
        <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
        <MenuItem onClick={handlePostMenuClose} sx={{ color: '#ff4444' }}>
          <ListItemIcon>
            <Flag fontSize="small" sx={{ color: '#ff4444' }} />
          </ListItemIcon>
          <ListItemText>Report Post</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Post Modal */}
      {community && (
        <CreatePostModal
          open={createPostModalOpen}
          onClose={() => setCreatePostModalOpen(false)}
          onSuccess={handlePostCreated}
          userCommunities={[{...community, isMember: true, userRole: community.userRole}]}
          preselectedCommunity={community}
        />
      )}

      {/* Edit Community Modal */}
      {community && (
        <EditCommunityModal
          open={editCommunityModalOpen}
          onClose={() => setEditCommunityModalOpen(false)}
          onSuccess={handleCommunityEdited}
          community={community}
        />
      )}
    </Box>
  );
};

export default CommunityDetailPage;